import {BaseElementAsync, BASIS, MsgLabel, OSkinableInit} from "back/commons/basis";
import {AppFrameDeskFeat, IAppCtx} from "back/core/appFrame";
import {IWspUiEnv, Wsp} from "lib/wsp/wsp";
import {IInfoBroker} from "lib/commons/infos";
import {AccelKeyMgr, Action, ActionMenuDep} from "lib/commons/actions";
import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {ChainUniverse, IChainEnv} from "lib/wsp/chain";
import {Desk} from "lib/commons/desk";
import {AutoMigrWspAction, CreateDrfWspAction, CreateDrvWspAction, DeleteWspAction, ExportWspAction, OpenPermsWspAction, OpenPropsWspAction, ScanFsUpdatesAction} from "back/wsp/actions/wspActions";
import {DOM, JSX, ShadowJsx} from "lib/commons/xml/dom";
import {IWedSearchCoordinatorPointer, WedSearchCoordinator} from "back/edit/wed/features/searchBar";
import {POPUP, PopupNotif} from "back/commons/widgets/popups";
import {AppHeader} from "back/core/widgets/appHeader";
import {VIEWS} from "lib/commons/views";
import {WSPPACK} from "lib/wsp/pack";
import {ButtonActions, OButtonActionsInit} from "back/commons/widgets/buttons";
import {ImportScar, OpenWspTrash} from "back/wsp/actions/srcActions";
import {WspMetaUi} from "lib/wsp/wspMetaUi";

export abstract class WspBaseApp extends BaseElementAsync {

	reg: IReg<IWspUiEnv>;
	universe: ChainUniverse;

	wsp: Wsp;

	infoBroker: IInfoBroker;

	accelKeyMgr: AccelKeyMgr<IRegPointer<IWspUiEnv>>;

	appErrorStatus: 'accessDenied';

	appHeader: AppHeader<any>;

	protected _config: IAppCtx<IChainEnv> & OSkinableInit;

	protected _wspTi: HTMLElement = <div id="wspTi"/>;
	protected _wspIcon: HTMLImageElement = <img id="wspIcon"/> as HTMLImageElement
	protected _wspTypeTi: HTMLElement = <div id="wspTypeTi"/>;
	protected _headTitleBar: HTMLElement =<div id="wspHeader"><ShadowJsx skin="wspBaseApp/headerBar"/></div>

	/** mémoire des derniers labels utilisés pour le label de la view. */
	protected _lastSrcLabel: string;

	protected _lastWspLabel: string;

	/** mémoire du dernier wspMetaUi pour forcer un reload si différent. */
	protected _lastWspMetaUi: WspMetaUi;

	/** @see IApp.getHeadTitleBar() */
	getHeadTitleBar(): HTMLElement {
		return this._headTitleBar;
	}

	/** Chargement de l'atelier */
	protected async _initWsp(init: IAppCtx<IChainEnv> & OSkinableInit, pendingUi: MsgLabel) {
		const sr = this.shadowRoot;
		BASIS.clearContent(sr);
		sr.appendChild(pendingUi);
		if (!this.universe.auth.currentUser) {
			pendingUi.setStandardMsg('needAuth');
			return false; //throw Error("Auth required");
		}

		if (!Desk.checkCompat('edit')) {
			pendingUi.setCustomMsg("");
			pendingUi.append(
				<div>
					<p>Ce navigateur n'est pas compatible avec l'édition de texte riche.</p>
					<p>Vous pouvez utiliser un des navigateurs à jour suivants :</p>
					<ul>
						{Desk.knownNavCompat.map((n) => n.url ? <li><a href={n.url} target="_blank">{n.name}</a></li> : <li>{n.name}</li>)}
					</ul>
				</div>
			);
			return false;
		}

		pendingUi.setStandardMsg('loading');

		//Chargement de l'atelier
		try {
			await this.wsp.waitForAvailable(this);
			this._lastWspMetaUi = this.wsp.wspMetaUi;
		} catch (e) {
			console.log(e);
			const wspTitle = this.wsp.wspTitle;
			if (this.wsp.isAccessDenied) {
				pendingUi.setCustomMsg(wspTitle ? `Accès à l'atelier '${wspTitle}' non autorisé.` : `Accès non autorisé à l'atelier.`, "error");
				return false;
			} else if (this.wsp.infoWsp && !this.wsp.wspMetaUi) {
				pendingUi.setCustomMsg("Échec au chargement du modèle documentaire de l'atelier.", "error");
				if (!await this.openPropsWspOnError(init)) return false;
			} else if (this.wsp.infoWspError) {
				if (this.wsp.infoWspError.status === 'noWsp') {
					pendingUi.setCustomMsg("L'atelier n'est pas (ou plus) disponible.", "error");
					return false;
				} else {
					pendingUi.setCustomMsg("Échec au chargement de l'atelier.", "error");
					if (!await this.openPropsWspOnError(init)) return false;
				}
			} else {
				pendingUi.setStandardMsg('netError');
				return false;
			}
		}
		if (this.wsp.isDeleted) {
			//Ajouter un param pour autoriser la consultation du wsp dans trash ?
			pendingUi.setCustomMsg("L'atelier a été supprimé.", "error");
			return false;
		}
		return true;
	}

	protected async openPropsWspOnError(init: IAppCtx<IChainEnv> & OSkinableInit) {
		const act = new OpenPropsWspAction();
		const ctx: IRegPointer<IWspUiEnv> = {reg: REG.createSubReg(init.reg, this.wsp.reg)}
		if (act.isAvailable(ctx)) {
			await act.execute(ctx);
			await this.wsp.waitForAvailable(this);
			this._lastWspMetaUi = this.wsp.wspMetaUi;
		}
		return this.wsp.isAvailable;
	}

	/** Init du registre et check perm. */
	protected async _initReg(init: IAppCtx<IChainEnv>, codeApp: string, perm: string, pendingUi: MsgLabel) {
		const reg = this.reg = REG.createSubReg(init.reg, this.wsp.reg);
		reg.env.uiRoot = this;
		reg.env.place = this.wsp.newPlaceWsp();
		reg.env.place.eventsMgr.on("wspLiveStateChange", this.onWspLiveStateChange.bind(this));
		reg.env.infoBroker = this.infoBroker;
		(this.reg.env as IWedSearchCoordinatorPointer).wedSearchCoord = new WedSearchCoordinator();

		await this.initRegExtPoints();
		if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return false; //raceCond

		// Injection d'init avant construction du contenu.
		const injectInits = reg.getList<(app: WspBaseApp) => void | Promise<void>>(`${codeApp}:init`);
		if (injectInits) for (const lstn of injectInits) {
			const result = lstn(this);
			if (result instanceof Promise) {
				await result;
				if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return false; //raceCond
			}
		}

		//check perm pour cette ihm.
		if (!reg.hasPerm(perm)) {
			pendingUi.setStandardMsg('accessDenied');
			this.appErrorStatus = 'accessDenied';
			return false;
		}

		//AccelKeyMgr
		if (!this.accelKeyMgr) {
			this.accelKeyMgr = new AccelKeyMgr();
			this.addEventListener('keydown', function (this: WspBaseApp, ev: KeyboardEvent) {
				this.accelKeyMgr.handleKeyboardEvent(ev, this)
			});
		} else {
			this.accelKeyMgr.reset();
		}
		this.accelKeyMgr.initFromMapActions(reg.getListAsMap(`accelkeys:${this.localName}:global`));
		return true;
	}

	protected _initAppHeader(init: IAppCtx<IChainEnv>, focusListening: HTMLElement[], actionsMain: string, actionsMore: string) {
		const reg = this.reg;
		this._wspIcon.src = this.wsp.wspMetaUi.getIcon() || "/@skin@/wsp/objects/wsp/wsp.svg";
		this._wspTypeTi.textContent = null;
		this._wspTypeTi.append(this._wspIcon, WSPPACK.buildWspDefTitle(this.wsp.infoWsp.wspType, {short: true, lang: true}));
		this._wspTypeTi.title = WSPPACK.buildWspDefTitle(this.wsp.infoWsp.wspType); //complet avec détail version.
		if (init.hasHeadTitleBar) {
			// appHeader masqué, actions visibles dans la HeadTitleBar
			this.appHeader = new AppHeader<WspBaseApp>().initialize({
				reg,
				actionContext: this,
				focusListening
			});

			//Version avec ButtonActions d'options à côté
			BASIS.clearContent(this._headTitleBar.shadowRoot);
			this._headTitleBar.shadowRoot.appendChild(<ButtonActions id="wspActions" î={{
				actions: reg.getList(actionsMain),
				actionContext: this,
				// icon: "/@skin@/commons/icons/actions.svg",
				title: "Actions sur cet atelier"
			} as OButtonActionsInit<WspBaseApp>}>
				<div>
					{this._wspTi}
					{this._wspTypeTi}
				</div>
			</ButtonActions>);
		} else {
			// appHeader visible
			// Ancienne approche - todo : graphisme à retravailler si nouvel usage...
			this.appHeader = new AppHeader<WspBaseApp>().initialize({
				reg,
				mainActions: reg.getList(actionsMain),
				moreActions: reg.getList(actionsMore),
				actionContext: this,
				focusListening
			});
			this.appHeader.appendChild(<div id="wspHeader"><ShadowJsx skin="wspBaseApp/headerBar">
				{this._wspTi}
				{this._wspTypeTi}
			</ShadowJsx></div>);
		}
	}

	protected _refreshTitle(srcLabel: string) {
		const wspLabel = this.wsp.wspTitle || "[Atelier non disponible]";
		DOM.setTextContent(this._wspTi, wspLabel);
		DOM.setAttr(this._wspTi, "title", wspLabel);
		DOM.setAttr(this, "label", srcLabel ? srcLabel + " - " + wspLabel : wspLabel);
		if (this._lastSrcLabel !== srcLabel || this._lastWspLabel !== wspLabel) VIEWS.dispatchViewChange(this);
		this._lastSrcLabel = srcLabel;
		this._lastWspLabel = wspLabel;
	}

	protected async onWspLiveStateChange(wsp: Wsp) {
		if (wsp !== this.wsp) return;
		if (this._lastWspMetaUi && this.wsp.wspMetaUi && this._lastWspMetaUi !== this.wsp.wspMetaUi) {
			//Changement des WspMetaUi, on reload
			await this.initializedAsync; // on séquentialise des onWspLiveStateChange concurrents.
			this.reloadApp();
			return;
		}
		this._refreshTitle(null);
		this._refreshMigration();
		this.appHeader.refresh();
		if (wsp.isDeleted === true) {
			if (this._PopupWspError) return;
			this._PopupWspError = POPUP.confirm("L'atelier a été supprimé", document.body, {kind: "forbidden", cancelLbl: null});
			await this._PopupWspError;
			this._PopupWspError = null;
			if (AppFrameDeskFeat.isIn(desk)) desk.gotoHome();
		} else if (wsp.isAccessDenied) {
			if (this._PopupWspError) return;
			this._PopupWspError = POPUP.confirm("Vous ne disposez plus d'accès à cet atelier", document.body, {kind: "forbidden", cancelLbl: null});
			await this._PopupWspError;
			this._PopupWspError = null;
			if (AppFrameDeskFeat.isIn(desk)) desk.gotoHome();
		} else if (wsp.isInError) {
			if (this._PopupWspError) return;
			this._PopupWspError = POPUP.confirm("La configuration de l'atelier est en erreur. Veuillez vérifier ses propriétés.", this, {kind: "error", cancelLbl: null});
			await this._PopupWspError;
			this._PopupWspError = null;
			if (AppFrameDeskFeat.isIn(desk)) desk.gotoHome();
			new OpenPropsWspAction().executeIfAvailable({reg: REG.createSubReg(this.reg, this.wsp.reg)});
		}
	}

	abstract reloadApp(): void

	protected _PopupWspError: Promise<boolean>

	protected _refreshMigration() {
		if (this._lastMigrPopup) this._lastMigrPopup.close();
		if (this.wsp.infoWsp && this.wsp.infoWsp.isMigrationNeeded) {
			this._lastMigrPopup = POPUP.showNotifWarning(<div>Migration des données de l'atelier nécessaire.</div>, this, {actions: [new AutoMigrWspAction().setLabel("Migrer")], actionContext: this});
			this._lastMigrPopup.onNextClose().then(() => {this._lastMigrPopup = null});
		}
	}

	protected _lastMigrPopup: PopupNotif | null;


	addGlobalAction(list: string, action: Action<IRegPointer<IWspUiEnv>>, sortKey: number, accel?: string, inShortDescAccelKey?: boolean) {
		this.reg.addToList(list, action.getId(), 1, action);
		if (accel) {
			this.reg.addToList(`accelkeys:${this.localName}:global`, accel, 1, action);
			if (inShortDescAccelKey) {
				//Quand l'action peut être plus pertinente lorsqu'un IShortDescCtx est pris en compte.
				this.reg.addToList("accelkeys:wsp:shortDesc", accel, 1, action)
			}
		}
	}

	protected abstract initRegExtPoints(): Promise<void>;

	protected addWspActionsMain(list: string, prefixSub: string) {
		this.addGlobalAction(list, new AutoMigrWspAction().setLabel("Migration nécessaire...").setIcon("/@skin@/wsp/apps/warning.svg"), 0);
		this.addGlobalAction(list, new ActionMenuDep('wspConfig').setActionLists(prefixSub + ":config").setLabel("Configuration"), 10);
		this.addGlobalAction(list, new ActionMenuDep('wspContent').setActionLists(prefixSub + ":content").setLabel("Contenu"), 20);
		this.addGlobalAction(list, new ActionMenuDep('wspLayers').setActionLists(prefixSub + ":layers").setLabel("Sous-ateliers calques"), 30);
		this.addGlobalAction(list, new ActionMenuDep('wspHelp').setActionLists(prefixSub + ":help actions:wsp:help").setLabel("Aide"), 40);
	}

	protected addWspActionsConfig(list: string) {
		this.addGlobalAction(list, new OpenPropsWspAction(), 10);
		this.addGlobalAction(list, new OpenPermsWspAction().requireVisiblePerm(["ui.apps.set.perms.wsp", "admin.node.configRoles.byAdmin"]), 20);
		this.addGlobalAction(list, new DeleteWspAction().requireVisiblePerm("ui.apps.delete.wsp"), 30);
	}

	protected addWspActionsContent(list: string) {
		this.addGlobalAction(list, new ScanFsUpdatesAction(), 5);
		this.addGlobalAction(list, new OpenWspTrash(), 10);
		this.addGlobalAction(list, new ExportWspAction(), 20);
		this.addGlobalAction(list, new ImportScar(), 30);
	}

	protected addWspActionsLayers(list: string) {
		this.addGlobalAction(list, new CreateDrfWspAction().requireVisiblePerm("ui.apps.create.wsp.drf"), 10);
		this.addGlobalAction(list, new CreateDrvWspAction().requireVisiblePerm("ui.apps.create.wsp.drv"), 20);
	}
}


REG.reg.registerSkin('wspBaseApp/headerBar', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
		font-size: 1rem;
		align-self: stretch;
		justify-content: start;
	}

	#wspTi, #wspTypeTi {
		display: block;
		max-width: 20em;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		text-align: start;
	}

	#wspTi {
		font-weight: bold;
		padding-bottom: .3em;
	}

	#wspTypeTi {
		font-size: 80%;
		color: var(--fade-color);
	}

	#wspIcon {
		height: 1.5em;
		vertical-align: middle;
		margin-inline-end: .5em;
	}

	#wspActions {
		align-self: stretch;
		padding-inline-start: .3em;
	}
`);